import React, { useEffect } from "react";
import { Row, Col, FormGroup } from "reactstrap";
import { IoCheckmarkCircle } from "react-icons/io5";

import { useFormik } from "formik";
import * as Yup from "yup";

import { LabelText } from "../../../Utils/Input";
import InputField from "../../../Utils/InputField";

const DISCOUNT_TYPES = [
  {
    value: "flat",
    icon: "₹",
    title: "Flat Amount",
    subtitle: "Fixed ₹ off",
  },
  {
    value: "percentage",
    icon: "%",
    title: "Percentage",
    subtitle: "% discount",
  },
];

function RewardForm({ initialData = {}, onDataChange }) {
  const formik = useFormik({
    enableReinitialize: true,

    initialValues: {
      pointsrequired: initialData?.pointsrequired ?? "",
      discounttype: initialData?.discounttype || "flat",
      discountvalue: initialData?.discountvalue ?? "",
      minOrderValue: initialData?.minOrderValue ?? "",
      startdate: initialData?.startdate || "",
      enddate: initialData?.enddate || "",
    },

    validationSchema: Yup.object({
      pointsrequired: Yup.number()
        .required("Required")
        .positive("Must be greater than 0"),

      discounttype: Yup.string().required("Required"),

      discountvalue: Yup.number()
        .required("Required")
        .positive("Must be greater than 0"),

      minOrderValue: Yup.number()
        .required("Required")
        .min(0, "Cannot be negative"),

      startdate: Yup.date().required("Required"),

      enddate: Yup.date()
        .required("Required")
        .min(Yup.ref("startdate"), "End date must be after start date"),
    }),

    onSubmit: () => {},
  });

  const { values, errors, touched, setFieldValue } = formik;

  useEffect(() => {
    onDataChange?.(values);
  }, [values, onDataChange]);

  const selectedType = DISCOUNT_TYPES.find(
    (type) => type.value === values.discounttype,
  );

  return (
    <div className="card border-0 shadow-sm rounded-4">
      {/* HEADER */}
      <div className="card-body p-4 pb-3">
        <h4 className="fw-bold mb-1">Reward Configuration</h4>

        <p className="text-muted mb-0">
          Define how many points are needed and what customers save.
        </p>
      </div>

      {/* FIELDS */}
      <div className="card-body border-top pt-4 pb-3">
        <Row className="gy-0">
          {/* POINTS */}
          <Col md={12}>
            <FormGroup className="mb-3">
              <LabelText
                displayText="Points Required to Redeem"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <InputField
                name="pointsrequired"
                type="text"
                placeholder="e.g. 100"
                value={values.pointsrequired}
                handleChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setFieldValue("pointsrequired", value);
                  }
                }}
                invalid={touched.pointsrequired && errors.pointsrequired}
                className="rounded-3"
              />

              {touched.pointsrequired && errors.pointsrequired && (
                <small className="text-danger">{errors.pointsrequired}</small>
              )}

              <small className="text-muted d-block mt-1">
                Customer must have at least this many points to redeem the
                offer.
              </small>
            </FormGroup>
          </Col>

          {/* DISCOUNT */}
          <Col md={12}>
            <FormGroup className="mb-3">
              <LabelText
                displayText="Discount Value"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <Row className="g-2 mb-3">
                {DISCOUNT_TYPES.map((type) => {
                  const isActive = values.discounttype === type.value;

                  return (
                    <Col md={6} key={type.value}>
                      <div
                        className="d-flex align-items-center justify-content-between rounded-3 p-3"
                        style={{
                          cursor: "pointer",

                          border: isActive
                            ? "1.5px solid #f97316"
                            : "1px solid #dee2e6",

                          background: isActive ? "#FFF3EE" : "#fff",
                        }}
                        onClick={() =>
                          setFieldValue("discounttype", type.value)
                        }
                      >
                        <div className="d-flex align-items-center gap-2">
                          <div
                            className="d-flex justify-content-center align-items-center rounded-3"
                            style={{
                              width: 36,
                              height: 36,

                              background: isActive ? "#f97316" : "#f1f3f5",

                              color: isActive ? "#fff" : "#495057",

                              fontWeight: 600,
                            }}
                          >
                            {type.icon}
                          </div>

                          <div>
                            <div
                              className="fw-semibold"
                              style={{
                                color: isActive ? "#f97316" : "#212529",
                              }}
                            >
                              {type.title}
                            </div>

                            <small className="text-muted">
                              {type.subtitle}
                            </small>
                          </div>
                        </div>

                        {isActive && (
                          <IoCheckmarkCircle size={20} color="#f97316" />
                        )}
                      </div>
                    </Col>
                  );
                })}
              </Row>

              <InputField
                name="discountvalue"
                type="text"
                placeholder="e.g. 15"
                value={values.discountvalue}
                handleChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setFieldValue("discountvalue", value);
                  }
                }}
                invalid={touched.discountvalue && errors.discountvalue}
                className="rounded-3"
              />

              {touched.discountvalue && errors.discountvalue && (
                <small className="text-danger">{errors.discountvalue}</small>
              )}
            </FormGroup>
          </Col>

          {/* MIN ORDER */}
          <Col md={12}>
            <FormGroup className="mb-3">
              <LabelText
                displayText="Minimum Order Value"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <InputField
                name="minOrderValue"
                type="text"
                placeholder="e.g. 100"
                value={values.minOrderValue}
                handleChange={(e) => {
                  const value = e.target.value;
                  if (/^\d*\.?\d*$/.test(value)) {
                    setFieldValue("minOrderValue", value);
                  }
                }}
                invalid={touched.minOrderValue && errors.minOrderValue}
                className="rounded-3"
              />

              {touched.minOrderValue && errors.minOrderValue && (
                <small className="text-danger">{errors.minOrderValue}</small>
              )}
            </FormGroup>
          </Col>

          {/* VALIDITY */}
          <Col md={12}>
            <FormGroup className="mb-0">
              <LabelText
                displayText="Validity Period"
                className="form-label fw-semibold mb-2 text-danger"
                important="*"
              />

              <Row className="g-2">
                {/* START */}
                <Col md={6}>
                  <div
                    className="rounded-3 overflow-hidden"
                    style={{
                      border:
                        touched.startdate && errors.startdate
                          ? "1px solid #dc3545"
                          : "1px solid #dee2e6",
                    }}
                  >
                    <div
                      className="px-3 pt-2"
                      style={{
                        background: "#f1f3f5",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6c757d",
                        textTransform: "uppercase",
                      }}
                    >
                      Start Date
                    </div>

                    <input
                      type="date"
                      name="startdate"
                      className="form-control border-0 px-3 pb-2"
                      style={{
                        background: "#f1f3f5",
                        boxShadow: "none",
                      }}
                      value={values.startdate}
                      onChange={(e) =>
                        setFieldValue("startdate", e.target.value)
                      }
                    />
                  </div>
                </Col>

                {/* END */}
                <Col md={6}>
                  <div
                    className="rounded-3 overflow-hidden"
                    style={{
                      border:
                        touched.enddate && errors.enddate
                          ? "1px solid #dc3545"
                          : "1px solid #dee2e6",
                    }}
                  >
                    <div
                      className="px-3 pt-2"
                      style={{
                        background: "#f1f3f5",
                        fontSize: 11,
                        fontWeight: 600,
                        color: "#6c757d",
                        textTransform: "uppercase",
                      }}
                    >
                      End Date
                    </div>

                    <input
                      type="date"
                      name="enddate"
                      className="form-control border-0 px-3 pb-2"
                      style={{
                        background: "#f1f3f5",
                        boxShadow: "none",
                      }}
                      value={values.enddate}
                      onChange={(e) => setFieldValue("enddate", e.target.value)}
                    />
                  </div>
                </Col>
              </Row>

              {((touched.startdate && errors.startdate) ||
                (touched.enddate && errors.enddate)) && (
                <small className="text-danger d-block mt-1">
                  {errors.startdate || errors.enddate}
                </small>
              )}
            </FormGroup>
          </Col>
        </Row>
      </div>
    </div>
  );
}

export default RewardForm;
